﻿// ============================================================================
// MÓDULO 0: APP.JSX (V80.0 - SINGLETON FIX)
// ARQUIVO: src/app/App.jsx
// OBJETIVO: Roteador Mestre Limpo. Remove inicialização duplicada do Firebase.
// ============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { browserEnv } from "../utils/browserEnv";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { isSignInWithEmailLink, multiFactor } from "firebase/auth";
import {
  Navbar,
  HeroSection,
  Footer,
  WhatsAppFloat,
  MobileBottomNav,
  CookieConsent,
  UnderConstruction,
} from "../components/layout/LegalDesignUI";
import { ENABLE_ACADEMIA_PUBLIC, ASSETS } from "../config/Config";

import { useOfficeContext } from "../hooks/useOfficeContext";

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[RDA] App crash:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center bg-white p-6">
          <div className="max-w-xl w-full rounded-2xl border border-red-200 bg-red-50 p-6">
            <div className="text-sm font-bold text-red-700">
              Erro ao carregar a página
            </div>
            <div className="mt-2 text-xs text-red-700 whitespace-pre-wrap">
              {String(this.state.error?.message || this.state.error)}
            </div>
            <div className="mt-4 text-[10px] text-red-700/80 font-mono">
              Dica: abra o Console do navegador para ver o stack trace completo.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Importações diretas (não lazy) para componentes críticos
import { PracticeAreas } from "../components/PracticeAreas";
import { AboutMe } from "../components/AboutMe";
import { PrivacyPolicy } from "../components/PrivacyPolicy";
import { AcademiaArea } from "../components/AcademiaArea";
import GuestViewer from "../components/GuestViewer";
import AuthActionHandler from "../components/AuthActionHandler";
import AutosDashboard from "../features/files/AutosDashboard";

import BridgeLayout from "../features/bridge/BridgeLayout";
import BridgeHome from "../features/bridge/BridgeHome";
import BridgeClients from "../features/bridge/BridgeClients";

// MÓDULOS CORE (IMPORTADOS, NÃO REDEFINIDOS)
import { ClientArea } from "../features/auth/components/ClientArea";
import { AdminDashboard } from "../pages/AdminDashboard";
import { UserDashboard } from "../pages/UserDashboard";
import PublicResearch from "../pages/PublicResearch";

import DocumentViewer from "../features/public/DocumentViewer";
import PecaStandaloneViewer from "../features/files/PecaStandaloneViewer";
import CadernoTrabalhoStandaloneViewer from "../features/files/CadernoTrabalhoStandaloneViewer";

import SetupAccount from "../pages/SetupAccount";
import AuthCallback from "../pages/portal/AuthCallback";
import ClientLogin from "../pages/portal/ClientLogin";
import ClientDashboard from "../pages/portal/ClientDashboard";
import IntakeForm from "../pages/public/IntakeForm";
import ClientProfile from "../pages/portal/ClientProfile";
import PortalCompletarCadastro from "../pages/portal/PortalCompletarCadastro";
import PortalProntuario from "../pages/portal/PortalProntuario";
import PortalProcessos from "../pages/portal/PortalProcessos";
import PortalVault from "../pages/portal/PortalVault";
import PortalCaso from "../pages/portal/PortalCaso";
import PortalFinanceiro from "../pages/portal/PortalFinanceiro";
import PortalAcademia from "../pages/portal/PortalAcademia";
import CanonicalHtmlViewer from "../pages/portal/CanonicalHtmlViewer";
import PortalLayout from "../components/layout/PortalLayout";
import PortalPreviewPage from "../pages/admin/PortalPreviewPage";

import { VerifyEmailScreen } from "../features/auth/components/VerifyEmailScreen";

import MfaSetup from "../pages/auth/MfaSetup";

// CORE LÓGICO (SINGLETON - A ÚNICA FONTE DE VERDADE)
import {
  subscribeToAuth,
  checkIsAdmin,
  logoutUser,
} from "../services/AuthRepository";
import { auth, functions } from "../shared/api/firebase/config";
import { httpsCallable } from "firebase/functions";
import { useCurrentUser } from "../hooks/useCurrentUser";

// Configura idioma
auth.languageCode = "pt";

const readEnrolledFactorCount = (user) => {
  if (!user) return 0;

  const directFactors = Array.isArray(user?.multiFactor?.enrolledFactors)
    ? user.multiFactor.enrolledFactors
    : null;

  if (directFactors) return directFactors.length;

  try {
    const factors = multiFactor(user)?.enrolledFactors;
    return Array.isArray(factors) ? factors.length : 0;
  } catch {
    return 0;
  }
};

// Mapa canônico de pathnames → page keys
// Usado APENAS no handleNavigate para pushState legado durante transição
const ROUTE_MAP = {
  "/portal/auth-callback": "portal-auth-callback",
  "/portal/login": "portal-login",
  "/portal/dashboard": "portal-dashboard",
  "/portal/caso": "portal-caso",
  "/portal/prontuario": "portal-caso",
  "/portal/processos": "portal-caso",
  "/portal/documentos": "portal-caso",
  "/portal/perfil": "portal-perfil",
  "/portal/financeiro": "portal-financeiro",
  "/portal/academia": "portal-academia",
  "/portal/vault": "portal-vault",
  "/portal/completar-cadastro": "portal-completar-cadastro",
  "/view-published": "canonical-html-viewer",
  "/novo-caso": "public-intake",
  "/setup-account": "setup-account",
  "/bridge": "bridge",
  "/bridge/file-hub": "bridge-file-hub",
  "/bridge/clients": "bridge-clients",
  "/acervo": "public-research",
  "/file-hub": "file-hub",
};

// Mapa canônico portal-key → page key
const PORTAL_KEY_MAP = {
  "portal-dashboard": "dashboard",
  "portal-caso": "caso",
  "portal-perfil": "perfil",
  "portal-financeiro": "financeiro",
  "portal-academia": "academia",
  "portal-vault": "vault",
};

const PORTAL_TABS = [
  { key: "dashboard", Component: ClientDashboard },
  { key: "caso", Component: PortalCaso },
  { key: "perfil", Component: ClientProfile },
  { key: "financeiro", Component: PortalFinanceiro },
  { key: "academia", Component: PortalAcademia },
  { key: "vault", Component: PortalVault },
];

function PortalTabKeepAlive({ activeKey }) {
  const [visited, setVisited] = React.useState(() => new Set([activeKey]));
  React.useEffect(() => {
    setVisited((prev) => {
      if (prev.has(activeKey)) return prev;
      return new Set([...prev, activeKey]);
    });
  }, [activeKey]);
  return (
    <>
      {PORTAL_TABS.map(({ key, Component }) => {
        if (!visited.has(key)) return null;
        return (
          <div
            key={key}
            className="portal-tab-enter"
            style={{ display: key === activeKey ? "block" : "none" }}
          >
            <Component />
          </div>
        );
      })}
    </>
  );
}

// --- APP PRINCIPAL ---
function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authLoadingSlow, setAuthLoadingSlow] = useState(false);

  const office = useOfficeContext();

  // Pré-carregamento leve de imagens (não bloqueante)
  useEffect(() => {
    if (ASSETS?.logoIcon) {
      const i = new Image();
      i.src = ASSETS.logoIcon;
    }
    if (ASSETS?.logoName) {
      const i = new Image();
      i.src = ASSETS.logoName;
    }
    if (ASSETS?.bannerHero) {
      const i = new Image();
      i.src = ASSETS.bannerHero;
    }
  }, []);

  // INTERCEPTADOR GLOBAL: Magic Link
  // Preserva location.search para que oobCode chegue intacto ao AuthCallback.
  // Guard de pathname evita loop infinito quando já estamos no callback.
  useEffect(() => {
    if (location.pathname === "/portal/auth-callback") return;
    const currentUrl = `${browserEnv.location.origin}${location.pathname}${location.search}${location.hash}`;
    if (isSignInWithEmailLink(auth, currentUrl)) {
      navigate(`/portal/auth-callback${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  // LIMPEZA DO MAGIC LINK: Remove oobCode da URL após processamento no callback
  // para evitar loop infinito de montagem em caso de refresh.
  useEffect(() => {
    if (location.pathname === "/portal/auth-callback" && location.search) {
      const timer = setTimeout(() => {
        navigate(location.pathname, { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.search, navigate]);

  // LISTENER DE AUTENTICAÇÃO (GLOBAL)
  useEffect(() => {
    const unsubscribe = subscribeToAuth((currentUser) => {
      setUser(currentUser);
      setIsAdmin(checkIsAdmin(currentUser));
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!authLoading) {
      setAuthLoadingSlow(false);
      return;
    }
    const t = setTimeout(() => setAuthLoadingSlow(true), 6000);
    return () => clearTimeout(t);
  }, [authLoading]);

  // Redirect cliente não-admin para portal após login
  useEffect(() => {
    if (!user || isAdmin) return;
    if (location.pathname === "/" || location.pathname === "/client") {
      navigate("/portal/dashboard", { replace: true });
    }
  }, [user, isAdmin, location.pathname, navigate]);

  const handleNavigate = useCallback(
    (page) => {
      browserEnv.scrollTo({ top: 0, left: 0 });
      // Mapeamento inverso: page key → pathname canônico
      const pageToPath = {
        homepage: "/",
        areas: "/areas",
        about: "/about",
        privacidade: "/privacidade",
        academia: "/academia",
        client: "/client",
        "portal-dashboard": "/portal/dashboard",
        "portal-caso": "/portal/caso",
        "portal-perfil": "/portal/perfil",
        "portal-financeiro": "/portal/financeiro",
        "portal-vault": "/portal/vault",
        "portal-login": "/portal/login",
        "portal-auth-callback": "/portal/auth-callback",
        "portal-completar-cadastro": "/portal/completar-cadastro",
        "public-research": "/acervo",
        "file-hub": "/file-hub",
        "public-intake": "/novo-caso",
        "setup-account": "/setup-account",
        "canonical-html-viewer": "/view-published",
        bridge: "/bridge",
        "bridge-file-hub": "/bridge/file-hub",
        "bridge-clients": "/bridge/clients",
      };
      const path = pageToPath[page];
      if (path) {
        navigate(path);
      } else {
        navigate(`/?page=${page}`);
      }
    },
    [navigate],
  );

  const handlePortalNavigate = useCallback(
    (key) => {
      const keyToPath = {
        dashboard: "/portal/dashboard",
        caso: "/portal/caso",
        perfil: "/portal/perfil",
        financeiro: "/portal/financeiro",
        academia: "/portal/academia",
        vault: "/portal/vault",
      };
      const path = keyToPath[key];
      if (path) {
        browserEnv.scrollTo({ top: 0, left: 0 });
        navigate(path);
      }
    },
    [navigate],
  );

  const handleBridgeNavigate = useCallback(
    (path) => {
      browserEnv.scrollTo({ top: 0, left: 0 });
      navigate(path);
    },
    [navigate],
  );

  const handleMfaResetConfirm = async (token, uid) => {
    setAuthLoading(true);
    try {
      const confirmFunc = httpsCallable(functions, "confirmMfaReset");
      const res = await confirmFunc({ token, uid });
      if (res.data.success) {
        alert("✅ SUCESSO! A autenticação de dois fatores foi removida.");
        navigate("/", { replace: true });
      }
    } catch (e) {
      alert("Erro ao confirmar reset: " + e.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const requiresAdminMfaEnrollment = Boolean(
    user && isAdmin && readEnrolledFactorCount(user) === 0,
  );

  const handleMfaEnrollmentComplete = useCallback(() => {
    const refreshedUser = user;
    setUser(refreshedUser);
    setIsAdmin(checkIsAdmin(refreshedUser));
    navigate("/client", { replace: true });
  }, [navigate]);

  // Derivar activePortalKey do pathname atual
  const activePortalKey =
    PORTAL_KEY_MAP[ROUTE_MAP[location.pathname]] || "dashboard";

  // Verificar contexto local/e2e
  const isLocalQaHost =
    browserEnv.exists() &&
    (window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "localhost");
  const isAutomatedE2E =
    typeof navigator !== "undefined" && navigator.webdriver === true;
  const shouldBypassAdminMfaEnrollment =
    Boolean(user && isAdmin) && (isLocalQaHost || isAutomatedE2E);

  // Guards globais de auth (renderizados antes das rotas)
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get("mode");
  const token = searchParams.get("token");

  if (token && !user) {
    if (!guestToken) setGuestToken(token);
  }

  // Detectar modo imersivo pelo pathname
  const pathname = location.pathname;
  const isBridgeMode = pathname.startsWith("/bridge");
  const isWordTaskpane = pathname.startsWith("/word");
  const isPortalMode =
    pathname.startsWith("/portal") || pathname === "/view-published";
  const isDocumentViewer = pathname.startsWith("/view/");
  const isPecaStandalone = pathname.startsWith("/peca/");
  const isIntakeMode = pathname === "/novo-caso";
  const isPreviewMode = searchParams.get("preview") === "1";
  const isClientMode = user && (pathname === "/client" || pathname === "/");
  const isImmersiveMode =
    Boolean(office?.isOffice) ||
    isWordTaskpane ||
    isClientMode ||
    isBridgeMode ||
    isPortalMode ||
    isIntakeMode ||
    isPreviewMode ||
    isDocumentViewer ||
    isPecaStandalone ||
    requiresAdminMfaEnrollment;

  const renderMain = () => {
    if (authLoading) {
      return (
        <div className="flex h-screen items-center justify-center bg-white">
          <div className="text-center">
            <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]"></div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Carregando autenticação...
            </div>
            {authLoadingSlow && (
              <div className="mt-3 text-[10px] text-gray-400 max-w-sm">
                Se esta tela não sair, pode haver erro de inicialização do
                Firebase/Auth. Abra o Console do navegador.
              </div>
            )}
          </div>
        </div>
      );
    }

    // Guards globais que se sobrepõem a qualquer rota
    if (
      (mode === "resetPassword" || mode === "verifyEmail") &&
      searchParams.get("oobCode")
    ) {
      return (
        <AuthActionHandler mode={mode} oobCode={searchParams.get("oobCode")} />
      );
    }
    if (user && !user.emailVerified && !isLocalQaHost && !isAdmin) {
      return <VerifyEmailScreen user={user} />;
    }
    if (requiresAdminMfaEnrollment && !shouldBypassAdminMfaEnrollment) {
      return (
        <MfaSetup
          user={user}
          onComplete={handleMfaEnrollmentComplete}
          onLogout={logoutUser}
        />
      );
    }

    return (
      <AppErrorBoundary>
        <Routes>
          {/* ── PORTAL DO CLIENTE ── */}
          <Route
            path="/portal/auth-callback"
            element={
              <AuthCallback
                onDone={(clientId) => {
                  if (clientId) {
                    try {
                      window.sessionStorage.setItem("portalClientId", clientId);
                    } catch {}
                  }
                  navigate("/portal/dashboard", { replace: true });
                }}
              />
            }
          />
          <Route path="/portal/login" element={<ClientLogin />} />
          <Route
            path="/portal/completar-cadastro"
            element={<PortalCompletarCadastro />}
          />
          <Route
            path="/portal/dashboard"
            element={
              <PortalLayout
                activeKey="dashboard"
                onNavigate={handlePortalNavigate}
                forcedClientId={
                  isAdmin ? searchParams.get("clientId") || null : null
                }
              >
                <PortalTabKeepAlive activeKey="dashboard" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/caso"
            element={
              <PortalLayout activeKey="caso" onNavigate={handlePortalNavigate}>
                <PortalTabKeepAlive activeKey="caso" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/prontuario"
            element={
              <PortalLayout activeKey="caso" onNavigate={handlePortalNavigate}>
                <PortalTabKeepAlive activeKey="caso" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/processos"
            element={
              <PortalLayout activeKey="caso" onNavigate={handlePortalNavigate}>
                <PortalTabKeepAlive activeKey="caso" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/perfil"
            element={
              <PortalLayout
                activeKey="perfil"
                onNavigate={handlePortalNavigate}
              >
                <PortalTabKeepAlive activeKey="perfil" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/financeiro"
            element={
              <PortalLayout
                activeKey="financeiro"
                onNavigate={handlePortalNavigate}
              >
                <PortalTabKeepAlive activeKey="financeiro" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/academia"
            element={
              <PortalLayout
                activeKey="academia"
                onNavigate={handlePortalNavigate}
              >
                <PortalTabKeepAlive activeKey="academia" />
              </PortalLayout>
            }
          />
          <Route
            path="/portal/vault"
            element={
              <PortalLayout activeKey="vault" onNavigate={handlePortalNavigate}>
                <PortalTabKeepAlive activeKey="vault" />
              </PortalLayout>
            }
          />

          {/* ── BRIDGE (WORD ADD-IN) ── */}
          <Route
            path="/bridge"
            element={
              <BridgeLayout
                title="RDA Studio"
                canGoBack={false}
                onHome={() => handleBridgeNavigate("/bridge")}
                onBack={() => handleBridgeNavigate("/bridge")}
              >
                <BridgeHome onNavigate={handleBridgeNavigate} />
              </BridgeLayout>
            }
          />
          <Route
            path="/bridge/file-hub"
            element={
              <BridgeLayout
                title="Organizador"
                canGoBack
                onHome={() => handleBridgeNavigate("/bridge")}
                onBack={() => handleBridgeNavigate("/bridge")}
              >
                <AutosDashboard />
              </BridgeLayout>
            }
          />
          <Route
            path="/bridge/clients"
            element={
              <BridgeLayout
                title="Clientes"
                canGoBack
                onHome={() => handleBridgeNavigate("/bridge")}
                onBack={() => handleBridgeNavigate("/bridge")}
              >
                <BridgeClients />
              </BridgeLayout>
            }
          />

          {/* ── PÚBLICO ── */}
          <Route path="/view/:id" element={<DocumentViewer />} />
          <Route path="/view-published" element={<CanonicalHtmlViewer />} />
          <Route path="/novo-caso" element={<IntakeForm />} />
          <Route
            path="/setup-account"
            element={<SetupAccount onNavigate={handleNavigate} />}
          />
          <Route path="/acervo" element={<PublicResearch />} />
          <Route path="/file-hub" element={<AutosDashboard />} />

          {/* ── ADMIN / CLIENTE AUTENTICADO ── */}
          <Route
            path="/peca/:workspaceId/:pieceId"
            element={
              user && isAdmin ? (
                <PecaStandaloneViewer />
              ) : (
                <HeroSection onNavigate={handleNavigate} />
              )
            }
          />
          <Route
            path="/caderno-trabalho/:workspaceId"
            element={
              user && isAdmin ? (
                <CadernoTrabalhoStandaloneViewer />
              ) : (
                <HeroSection onNavigate={handleNavigate} />
              )
            }
          />
          <Route
            path="/client"
            element={
              user && isAdmin ? (
                <AdminDashboard user={user} onNavigate={handleNavigate} />
              ) : (
                <ClientArea user={user} />
              )
            }
          />

          {/* ── HOMEPAGE E PÁGINAS PÚBLICAS ── */}
          <Route path="/areas" element={<PracticeAreas />} />
          <Route path="/about" element={<AboutMe />} />
          <Route path="/privacidade" element={<PrivacyPolicy />} />
          <Route
            path="/academia"
            element={
              isLocalQaHost ? (
                <AcademiaArea />
              ) : (
                <UnderConstruction onNavigate={handleNavigate} />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                isAdmin ? (
                  <AdminDashboard user={user} onNavigate={handleNavigate} />
                ) : (
                  <PortalLayout
                    activeKey="dashboard"
                    onNavigate={handlePortalNavigate}
                  >
                    <PortalTabKeepAlive activeKey="dashboard" />
                  </PortalLayout>
                )
              ) : (
                <HeroSection onNavigate={handleNavigate} />
              )
            }
          />
          <Route
            path="*"
            element={<HeroSection onNavigate={handleNavigate} />}
          />
        </Routes>
      </AppErrorBoundary>
    );
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-gray-800 bg-gray-50">
      {!isImmersiveMode && (
        <Navbar onNavigate={handleNavigate} user={user} onLogout={logoutUser} />
      )}
      <main className="flex-grow">{renderMain()}</main>
      {!isImmersiveMode && <Footer onNavigate={handleNavigate} />}
      {!isImmersiveMode && <WhatsAppFloat />}
      {!isImmersiveMode && <CookieConsent />}
      {!isImmersiveMode && (
        <MobileBottomNav
          onNavigate={handleNavigate}
          activeTab={location.pathname}
        />
      )}
    </div>
  );
}

export default App;
